/**
 * "Disparos": a lista dos modelos de WhatsApp que a regra de disparo mandou —
 * quando saiu, se chegou, se responderam e, quando não saiu, por quê. Pedido do
 * piloto em 01/10 ("ter um lugar com disparos"), depois de a Blip passar a
 * avisar a entrega.
 *
 * O período, a mesa, o status e a página vivem na URL (o link "Ver disparos"
 * do painel da mesa já chega filtrado). O período é o do MOVIMENTO mais recente
 * de cada disparo, em Brasília; sem escolha, hoje e os 7 dias anteriores.
 *
 * Sem telefone, de propósito: a lista é da equipe e o ticket já o tem.
 */
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Hand } from 'lucide-react';
import {
  TICKET_DISPATCH_STATUS_LABELS,
  TICKET_DISPATCH_STATUSES,
  TicketDispatchStatusSchema,
  capitalize,
  dispatchReasonLabel,
  dispatchReasonOf,
  isValidDateKey,
  resolveDispatchListRange,
  type DispatchListItem,
} from '@erp/shared';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge, EmptyState, Skeleton } from '@/components/ui/misc';
import { pageCountOf, Pagination } from '@/components/ui/pagination';
import { useTerms } from '@/shared/tenant-profile';
import { DeskDispatchSummary } from '@/features/desks/desk-dispatch-section';
import {
  DISPATCH_LIST_PAGE_SIZE,
  dispatchDesksOptions,
  dispatchListOptions,
} from '@/features/tickets/dispatch-queries';
import { DISPATCH_STATUS_VARIANT } from '@/features/tickets/ticket-dispatch-badge';
import { dispatchTime } from '@/features/tickets/ticket-dispatch-block';
import { errorMessage } from '@/features/tickets/ticket-format';
import { ticketsBack } from '@/features/tickets/tickets-back';

// validateSearch roda no router — puro
const dateKey = z.string().refine(isValidDateKey);

export const dispatchesSearchSchema = z.object({
  desk: z.coerce.number().int().positive().optional().catch(undefined),
  status: TicketDispatchStatusSchema.optional().catch(undefined),
  from: dateKey
    .default(() => resolveDispatchListRange({}).from)
    .catch(() => resolveDispatchListRange({}).from),
  to: dateKey
    .default(() => resolveDispatchListRange({}).to)
    .catch(() => resolveDispatchListRange({}).to),
  page: z.coerce.number().int().min(1).default(1).catch(1),
});
export type DispatchesSearch = z.infer<typeof dispatchesSearchSchema>;

const fullTime = (at: Date | string) => new Date(at).toLocaleString('pt-BR');

function Time({ at }: { at: Date | string | null }) {
  if (!at) return <span className="text-muted-foreground/60">—</span>;
  return (
    <time dateTime={new Date(at).toISOString()} title={fullTime(at)} className="tabular-nums">
      {dispatchTime(at)}
    </time>
  );
}

export function DispatchesPage() {
  const search = useSearch({ from: '/_authenticated/tickets/dispatches' });
  const navigate = useNavigate();
  const terms = useTerms();
  // "← Tickets" volta à última lista desta aba (mesa, visão e busca como estavam)
  const historyIndex = useRouterState({ select: (s) => s.location.state.__TSR_index });
  const back = ticketsBack(historyIndex);

  const desksQ = useQuery(dispatchDesksOptions());
  // período invertido (datas digitadas fora de ordem): nem pergunta — a API
  // responderia 422, e a tela já diz o que fazer
  const inverted = search.from > search.to;
  const listQ = useQuery({
    ...dispatchListOptions({
      from: search.from,
      to: search.to,
      desk: search.desk,
      status: search.status,
      page: search.page,
    }),
    enabled: !inverted,
  });

  /** filtro novo recomeça da primeira página */
  const setFilters = (patch: Partial<Omit<DispatchesSearch, 'page'>>) =>
    void navigate({ to: '/tickets/dispatches', search: { ...search, ...patch, page: 1 } });
  const goToPage = (page: number) =>
    void navigate({ to: '/tickets/dispatches', search: { ...search, page } });

  const desks = desksQ.data ?? [];
  const summaryDesks =
    search.desk !== undefined ? desks.filter((d) => d.id === search.desk) : desks;
  const total = listQ.data?.total ?? 0;
  const items = listQ.data?.items ?? [];
  const pageCount = pageCountOf(total, DISPATCH_LIST_PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Link
          to="/tickets"
          search={back.search}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          {capitalize(terms.tickets)}
        </Link>
        <h1 className="text-xl leading-7 font-semibold">Disparos</h1>
        <p className="text-[13px] text-muted-foreground">
          O modelo de WhatsApp de cada {terms.ticket}: quando entrou na fila, quando saiu, se foi
          entregue e se responderam.
        </p>
      </div>

      {/* o painel do dia de cada mesa com regra (o mesmo da config da mesa) */}
      {desksQ.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : desksQ.isError ? (
        <p className="text-sm text-destructive">{errorMessage(desksQ.error)}</p>
      ) : summaryDesks.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {summaryDesks.map((d) => (
            <DeskDispatchSummary key={d.id} deskId={d.id} deskName={d.name} />
          ))}
        </div>
      ) : (
        <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground shadow-xs">
          Nenhuma mesa tem disparo configurado. A regra fica em Configurações › Mesas de serviço ›
          Disparo.
        </p>
      )}

      {/* filtros */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4 shadow-xs">
        <div className="flex min-w-48 flex-col gap-1.5">
          <Label htmlFor="disp-desk" className="text-[13px]">
            Mesa
          </Label>
          <Select
            id="disp-desk"
            className="h-8"
            value={search.desk !== undefined ? String(search.desk) : ''}
            onChange={(e) =>
              setFilters({ desk: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value="">Todas as mesas</option>
            {desks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {d.enabled ? '' : ' (desligado)'}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex min-w-48 flex-col gap-1.5">
          <Label htmlFor="disp-status" className="text-[13px]">
            Status
          </Label>
          <Select
            id="disp-status"
            className="h-8"
            value={search.status ?? ''}
            onChange={(e) =>
              setFilters({
                status: e.target.value
                  ? TicketDispatchStatusSchema.parse(e.target.value)
                  : undefined,
              })
            }
          >
            <option value="">Todos os status</option>
            {TICKET_DISPATCH_STATUSES.map((s) => (
              <option key={s} value={s}>
                {TICKET_DISPATCH_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="disp-from" className="text-[13px]">
            De
          </Label>
          <Input
            id="disp-from"
            type="date"
            className="h-8 w-40"
            value={search.from}
            onChange={(e) => {
              if (e.target.value) setFilters({ from: e.target.value });
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="disp-to" className="text-[13px]">
            Até
          </Label>
          <Input
            id="disp-to"
            type="date"
            className="h-8 w-40"
            value={search.to}
            onChange={(e) => {
              if (e.target.value) setFilters({ to: e.target.value });
            }}
          />
        </div>
        <p className="pb-1.5 text-xs text-muted-foreground">
          Pelo movimento mais recente de cada disparo, no horário de Brasília.
        </p>
      </div>

      {inverted ? (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState message="O período termina antes de começar — ajuste as datas." />
        </div>
      ) : listQ.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : listQ.isError ? (
        <div className="rounded-lg border bg-card p-4 text-sm text-destructive shadow-xs">
          {errorMessage(listQ.error)}
        </div>
      ) : total === 0 ? (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState
            className="h-52"
            message="Nenhum disparo no período e nos filtros escolhidos"
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px] whitespace-nowrap">
                  <th className="h-9 px-3 font-semibold">{capitalize(terms.ticket)}</th>
                  <th className="h-9 px-3 font-semibold">{capitalize(terms.contact)}</th>
                  <th className="h-9 px-3 font-semibold">{capitalize(terms.client)}</th>
                  <th className="h-9 px-3 font-semibold">Status</th>
                  <th className="h-9 px-3 font-semibold">Disparado</th>
                  <th className="h-9 px-3 font-semibold">Entregue</th>
                  <th className="h-9 px-3 font-semibold">Respondeu</th>
                  <th className="h-9 px-3 font-semibold">Motivo</th>
                  <th className="h-9 px-3 font-semibold">Origem</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <DispatchRow key={item.id} item={item} showDesk={desks.length > 1} />
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 ? (
            // página além da última (URL editada, ou o período encolheu)
            <EmptyState className="h-32" message="Nenhum disparo nesta página" />
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
            <span className="text-[13px] text-muted-foreground">
              {total} disparo{total === 1 ? '' : 's'} — página {search.page} de {pageCount}
            </span>
            <Pagination page={search.page} pageCount={pageCount} onPage={goToPage} />
          </div>
        </div>
      )}
    </div>
  );
}

function DispatchRow({ item, showDesk }: { item: DispatchListItem; showDesk: boolean }) {
  const terms = useTerms();
  // sem telefone, diz onde o disparo procurou (a mesma lista que o envio percorre)
  const reason = dispatchReasonLabel(dispatchReasonOf(item), terms, item.phoneSources);
  const waiting = item.status === 'pending' && item.postponedUntil !== null;
  return (
    <tr className="border-t border-divider align-top">
      <td className="px-3 py-2.5 whitespace-nowrap">
        <Link
          to="/tickets/$ticketNumber"
          params={{ ticketNumber: String(item.ticketNumber) }}
          className="font-medium text-primary tabular-nums hover:underline"
          title={item.ticketTitle}
        >
          #{item.ticketNumber}
        </Link>
        {showDesk ? <div className="text-xs text-muted-foreground">{item.desk.name}</div> : null}
      </td>
      <td className="min-w-36 px-3 py-2.5">
        {item.contactName ?? (
          <span className="text-muted-foreground" title="O título não traz o nome">
            {item.ticketTitle}
          </span>
        )}
      </td>
      <td className="max-w-44 truncate px-3 py-2.5" title={item.clientName ?? undefined}>
        {item.clientName ?? '—'}
      </td>
      <td className="px-3 py-2.5">
        <Badge variant={DISPATCH_STATUS_VARIANT[item.status]}>
          {TICKET_DISPATCH_STATUS_LABELS[item.status]}
        </Badge>
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <Time at={item.sentAt} />
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <Time at={item.deliveredAt} />
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <Time at={item.repliedAt} />
      </td>
      <td className="min-w-48 max-w-72 px-3 py-2.5 text-xs">
        {reason ? (
          <span className={item.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}>
            {reason}
            {waiting ? ` — volta às ${dispatchTime(item.postponedUntil!)}` : ''}
          </span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap text-muted-foreground">
        {item.manual ? (
          <span className="inline-flex items-center gap-1" title="“Disparar agora”, à mão">
            <Hand className="size-3" aria-hidden />
            Manual
          </span>
        ) : (
          'Fila'
        )}
      </td>
    </tr>
  );
}
